import ProfileHeader from "@features/profile/components/profile-header";
import ProfileSection from "@features/profile/components/profile-section";
import { themes } from "@shared/config/themes";
import type {
	DateFormat,
	Locale,
	SidebarCollapsible,
	SidebarVariant,
} from "@shared/store/settings.store";
import { type Theme, useSettingsStore } from "@shared/store/settings.store";
import { Card, CardContent } from "@shared/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";
import { Switch } from "@shared/ui/switch";
import { format } from "date-fns";
import { FormattedMessage, useIntl } from "react-intl";
import { DATE_FNS_LOCALES, DATE_FORMATS } from "../../shared/config/constants";

export default function SettingsPage() {
	const {
		locale,
		theme,
		sidebarCollapsible,
		sidebarVariant,
		headerVariant,
		dateFormat,
		setLocale,
		setTheme,
		setSidebarCollapsible,
		setSidebarVariant,
		setHeaderVariant,
		setDateFormat,
	} = useSettingsStore();

	const intl = useIntl();

	return (
		<div className="space-y-6">
			<ProfileHeader
				title={intl.formatMessage({
					id: "page.profile.settings.title",
					defaultMessage: "Settings",
				})}
				description={intl.formatMessage({
					id: "page.profile.settings.subTitle",
					defaultMessage: "Configure the language and theme of the interface.",
				})}
			/>
			<Card>
				<CardContent>
					<div className="space-y-6">
						<ProfileSection
							name="locale"
							title={intl.formatMessage({
								id: "page.profile.settings.language",
								defaultMessage: "Language",
							})}
							description={intl.formatMessage({
								id: "page.profile.settings.languageDescription",
								defaultMessage: "Select the app language",
							})}
						>
							<Select
								value={locale}
								onValueChange={(value) => {
									if (value) setLocale(value as Locale);
								}}
							>
								<SelectTrigger className="w-48">
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="p-2 rounded-md">
									<SelectItem value="ru">
										<FormattedMessage
											defaultMessage="Russian"
											id="page.profile.settings.language.ru"
										/>
									</SelectItem>
									<SelectItem value="en">
										<FormattedMessage
											defaultMessage="English"
											id="page.profile.settings.language.en"
										/>
									</SelectItem>
								</SelectContent>
							</Select>
						</ProfileSection>
						<ProfileSection
							name="theme"
							title={intl.formatMessage({
								id: "page.profile.settings.theme",
								defaultMessage: "Design theme",
							})}
							description={intl.formatMessage({
								id: "page.profile.settings.themeField",
								defaultMessage: "Select a color theme",
							})}
						>
							<div className="flex gap-3">
								{(Object.entries(themes) as [Theme, { label: string; preview: string }][]).map(
									([key, value]) => (
										<button
											key={key}
											type="button"
											onClick={() => setTheme(key)}
											className={`flex flex-col items-center gap-2 rounded-lg border-2 p-3 transition-colors ${
												theme === key
													? "border-primary"
													: "border-border hover:border-muted-foreground"
											}`}
										>
											<div
												className="h-10 w-16 rounded-md ring-1 ring-foreground/10"
												style={{ backgroundColor: value.preview }}
											/>
											<span className="text-xs font-medium">{value.label}</span>
										</button>
									)
								)}
							</div>
						</ProfileSection>
						<ProfileSection
							name="locale"
							title={intl.formatMessage({
								id: "page.profile.settings.dateFormat",
								defaultMessage: "Date format",
							})}
							description={intl.formatMessage({
								id: "page.profile.settings.dateFormatDescription",
								defaultMessage: "How to display dates",
							})}
						>
							<Select
								value={dateFormat}
								onValueChange={(value) => {
									if (value) setDateFormat(value as DateFormat);
								}}
							>
								<SelectTrigger className="w-48">
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="p-2 rounded-md">
									{DATE_FORMATS.map((dateFormat) => (
										<SelectItem key={dateFormat.id} value={dateFormat.format}>
											{format(new Date(), dateFormat.format, { locale: DATE_FNS_LOCALES[locale] })}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</ProfileSection>

						<ProfileSection
							name="locale"
							title={intl.formatMessage({
								id: "page.profile.settings.sidebarCollapsible",
								defaultMessage: "Sidebar Collapsible",
							})}
							description={intl.formatMessage({
								id: "page.profile.settings.sidebarCollapsibleDescription",
								defaultMessage: "Side menu collapse option",
							})}
						>
							<Select
								value={sidebarCollapsible}
								onValueChange={(value) => {
									if (value) setSidebarCollapsible(value as SidebarCollapsible);
								}}
							>
								<SelectTrigger className="w-48">
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="p-2 rounded-md">
									<SelectItem value="icon">
										<FormattedMessage
											defaultMessage="Icon"
											id="page.profile.settings.sidebarCollapsible.icon"
										/>
									</SelectItem>
									<SelectItem value="offcanvas">
										<FormattedMessage
											defaultMessage="Offcanvas"
											id="page.profile.settings.sidebarCollapsible.offcanvas"
										/>
									</SelectItem>
									<SelectItem value="none">
										<FormattedMessage
											defaultMessage="None"
											id="page.profile.settings.sidebarCollapsible.none"
										/>
									</SelectItem>
								</SelectContent>
							</Select>
						</ProfileSection>

						<ProfileSection
							name="locale"
							title={intl.formatMessage({
								id: "page.profile.settings.sidebarVariant",
								defaultMessage: "Sidebar Variant",
							})}
							description={intl.formatMessage({
								id: "page.profile.settings.sidebarVariantDescription",
								defaultMessage: "Side menu display option",
							})}
						>
							<Select
								value={sidebarVariant}
								onValueChange={(value) => {
									if (value) setSidebarVariant(value as SidebarVariant);
								}}
							>
								<SelectTrigger className="w-48">
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="p-2 rounded-md">
									<SelectItem value="sidebar">
										<FormattedMessage
											defaultMessage="Sidebar"
											id="page.profile.settings.sidebarVariant.sidebar"
										/>
									</SelectItem>
									<SelectItem value="floating">
										<FormattedMessage
											defaultMessage="Floating"
											id="page.profile.settings.sidebarVariant.floating"
										/>
									</SelectItem>
									<SelectItem value="inset">
										<FormattedMessage
											defaultMessage="Inset"
											id="page.profile.settings.sidebarVariant.inset"
										/>
									</SelectItem>
								</SelectContent>
							</Select>
						</ProfileSection>
						<ProfileSection
							name="locale"
							title={intl.formatMessage({
								id: "page.profile.settings.headerSticky",
								defaultMessage: "Header sticky",
							})}
							description={intl.formatMessage({
								id: "page.profile.settings.headerStickyDescription",
								defaultMessage: "Change the header property to “Sticky”",
							})}
						>
							<Switch
								checked={headerVariant === "sticky"}
								onCheckedChange={(value) => {
									if (value) {
										setHeaderVariant("sticky");
									} else {
										setHeaderVariant("default");
									}
									return undefined;
								}}
							/>
						</ProfileSection>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
